import { Link } from "react-router-dom";
import { jsonLdString } from "../jsonLd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import type { ReactNode } from "react";
import GuideSection from "./GuideSection";

type SectionShape = {
    heading: string;
    intro?: string;
    items: { title: string; body: string }[];
};

type ArticleSchema = {
    slug: string;
    datePublished: string;
    dateModified?: string;
    image?: string;
    description?: string;
};

type HeroImage = {
    src: string;
    alt: string;
};

type Props = {
    eyebrow?: string;
    title: string;
    lede?: ReactNode;
    sections?: SectionShape[];
    children?: ReactNode;
    wide?: boolean;
    /** When set, emits a Schema.org Article JSON-LD into the document. */
    articleSchema?: ArticleSchema;
    /** Hero photo shown beside the title/lede in a full-bleed cream band,
     *  with a byline avatar. Opt-in — omitting it keeps the plain hero
     *  used by every other guide. */
    heroImage?: HeroImage;
    /** Striped placeholder + caption shown in the hero photo slot when no
     *  real photo exists yet. Takes the same slot as `heroImage` — set
     *  at most one of the two. */
    heroPlaceholder?: string;
    /** Full-bleed content (e.g. an opinion-lead block) inserted between
     *  the hero and the mapped `sections`. Only rendered when `heroImage`
     *  or `heroPlaceholder` is also set, since it shares that band's
     *  full-bleed treatment. */
    beforeSections?: ReactNode;
};

const SITE_ORIGIN = "https://www.planamoto.com";
const PUBLISHER = {
    "@type": "Organization",
    name: "Planamoto",
    url: SITE_ORIGIN,
    logo: {
        "@type": "ImageObject",
        url: `${SITE_ORIGIN}/logos/planamoto-favicon.svg`,
    },
};
// Named author for every guide. The `url` resolves to /about, which
// carries the matching Person JSON-LD — Google and LLM entity graphs
// need the round-trip to treat "Spencer" as a real, cite-worthy person
// rather than a boilerplate byline. Do not switch back to the
// Organization block; YMYL-adjacent content (legal kit, insurance,
// security) needs human authorship signals to earn E-E-A-T.
const AUTHOR = {
    "@type": "Person",
    name: "Spencer",
    url: `${SITE_ORIGIN}/about`,
};

const buildArticleLd = (
    title: string,
    schema: ArticleSchema,
): Record<string, unknown> => {
    const url = `${SITE_ORIGIN}/guides/${schema.slug}`;
    const ld: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: title,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        url,
        datePublished: schema.datePublished,
        dateModified: schema.dateModified ?? schema.datePublished,
        author: AUTHOR,
        publisher: PUBLISHER,
    };
    if (schema.description) ld.description = schema.description;
    if (schema.image) {
        ld.image = schema.image.startsWith("http")
            ? schema.image
            : `${SITE_ORIGIN}${schema.image}`;
    }
    return ld;
};

// "6 June 2026" — long-day, long-month form. Matches how a reader would
// speak the date and stays unambiguous across en-GB / en-US locales.
const formatByDate = (iso: string): string => {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });
};

// Breadcrumb LD: Home → Guides → <this guide>. Helps Google render a
// breadcrumb in the search snippet instead of the raw URL path.
const buildBreadcrumbLd = (
    title: string,
    slug: string,
): Record<string, unknown> => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN },
        { "@type": "ListItem", position: 2, name: "Guides", item: `${SITE_ORIGIN}/guides` },
        { "@type": "ListItem", position: 3, name: title, item: `${SITE_ORIGIN}/guides/${slug}` },
    ],
});

// Standard wrapper for every guide detail page: back-link to /guides,
// optional eyebrow, h1, lede, then the sections array rendered through
// GuideSection. Any closing CTA / sidebar lives in `children`. When
// `articleSchema` is set, also emits a Schema.org Article JSON-LD block
// so Google can classify the page as an article (eligible for rich
// results and an indexing boost over plain web pages).
export default function GuideArticle({
    eyebrow,
    title,
    lede,
    sections,
    children,
    wide = false,
    articleSchema,
    heroImage,
    heroPlaceholder,
    beforeSections,
}: Props) {
    const className = wide
        ? "gt-guide-article gt-guide-article--wide"
        : "gt-guide-article";

    const schemaScripts = articleSchema && (
        <>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: jsonLdString(buildArticleLd(title, articleSchema)),
                }}
            />
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: jsonLdString(
                        buildBreadcrumbLd(title, articleSchema.slug),
                    ),
                }}
            />
        </>
    );

    const backLink = (
        <Link to="/guides" className="gt-guide-article__back">
            <ArrowLeftOutlined /> All guides
        </Link>
    );
    const eyebrowEl = eyebrow && <div className="gt-eyebrow">{eyebrow}</div>;
    const titleEl = <h1 className="gt-h1 gt-guide-article__title">{title}</h1>;

    // Opt-in redesigned hero: full-bleed cream band, byline avatar, photo
    // beside the copy. Every other guide keeps the plain hero below.
    if (heroImage || heroPlaceholder) {
        return (
            <>
                {schemaScripts}
                <div className="gt-guide-hero-band">
                    <div className="gt-guide-hero-band__inner">
                        <div className="gt-guide-hero-band__head">
                            {backLink}
                            {eyebrowEl}
                            {titleEl}
                            {articleSchema && (
                                <p className="gt-guide-article__byline gt-guide-hero-band__byline">
                                    <span
                                        className="gt-guide-hero-band__avatar"
                                        aria-hidden="true"
                                    >
                                        S
                                    </span>
                                    <span>
                                        By <Link to="/about">Spencer</Link>
                                        {" · Published "}
                                        <time dateTime={articleSchema.datePublished}>
                                            {formatByDate(articleSchema.datePublished)}
                                        </time>
                                        {articleSchema.dateModified &&
                                            articleSchema.dateModified !==
                                                articleSchema.datePublished && (
                                                <>
                                                    {" · Updated "}
                                                    <time dateTime={articleSchema.dateModified}>
                                                        {formatByDate(articleSchema.dateModified)}
                                                    </time>
                                                </>
                                            )}
                                    </span>
                                </p>
                            )}
                        </div>
                        <div className="gt-guide-hero-band__photo">
                            {heroImage ? (
                                <img src={heroImage.src} alt={heroImage.alt} />
                            ) : (
                                <div className="gt-guide-hero-band__photo-placeholder">
                                    <span>PHOTO — {heroPlaceholder}</span>
                                </div>
                            )}
                        </div>
                        {lede && (
                            <p className="gt-guide-article__lede gt-guide-hero-band__lede">
                                {lede}
                            </p>
                        )}
                    </div>
                </div>
                {beforeSections}
                {sections?.map((s, i) => (
                    <GuideSection
                        key={s.heading}
                        index={i + 1}
                        heading={s.heading}
                        intro={s.intro}
                        items={s.items}
                    />
                ))}
                <div className={`${className} gt-guide-article--trailing`}>
                    {children}
                </div>
            </>
        );
    }

    return (
        <article className={className}>
            {schemaScripts}
            {backLink}
            {eyebrowEl}
            {titleEl}
            {articleSchema && (
                <p className="gt-guide-article__byline">
                    By <Link to="/about">Spencer</Link>
                    {" · Published "}
                    <time dateTime={articleSchema.datePublished}>
                        {formatByDate(articleSchema.datePublished)}
                    </time>
                    {articleSchema.dateModified &&
                        articleSchema.dateModified !== articleSchema.datePublished && (
                            <>
                                {" · Updated "}
                                <time dateTime={articleSchema.dateModified}>
                                    {formatByDate(articleSchema.dateModified)}
                                </time>
                            </>
                        )}
                </p>
            )}
            {lede && <p className="gt-guide-article__lede">{lede}</p>}
            {sections?.map((s, i) => (
                <GuideSection
                    key={s.heading}
                    index={i + 1}
                    heading={s.heading}
                    intro={s.intro}
                    items={s.items}
                />
            ))}
            {children}
        </article>
    );
}
